// VOID Conflict / UCG — Community Transmissions & Inbox Manager
// Orchestrates Developer Messages, Gift Drops, Voucher Redemptions, and Unread Status.

import { getAuth, handleUnauthorizedIdentity } from './playerIdentity.js';
import { wallet } from '../engine/wallet.js';
import { inventory } from '../engine/inventory.js';
import { schedulePush } from './cloudSync.js';

class InboxManager {
  constructor() {
    this.unreadCount = 0;
    this.unclaimedGifts = 0;
    this.hasHighPriority = false;
    this.messages = [];
    this._listeners = new Set();
    this._isChecking = false;
    this._lastCheckTime = 0;
    this._checkTtlMs = 45000; // 45s TTL cache prevents network hammering on rapid menu transit
  }

  addListener(fn) {
    if (typeof fn === 'function') {
      this._listeners.add(fn);
    }
  }

  removeListener(fn) {
    this._listeners.delete(fn);
  }

  _notify() {
    for (const fn of this._listeners) {
      try {
        fn({
          unreadCount: this.unreadCount,
          unclaimedGifts: this.unclaimedGifts,
          hasHighPriority: this.hasHighPriority,
          hasNewData: (this.unreadCount > 0 || this.unclaimedGifts > 0)
        });
      } catch (err) {
        console.warn('[InboxManager] Listener error:', err);
      }
    }
  }

  hasNewData() {
    return this.unreadCount > 0 || this.unclaimedGifts > 0;
  }

  async checkInbox({ force = false } = {}) {
    if (this._isChecking) return null;
    const now = Date.now();
    if (!force && this._lastCheckTime > 0 && (now - this._lastCheckTime < this._checkTtlMs)) {
      return {
        unreadCount: this.unreadCount,
        unclaimedGifts: this.unclaimedGifts,
        hasHighPriority: this.hasHighPriority
      };
    }
    const auth = getAuth();
    if (!auth?.accountId || !auth?.secret || auth.isExpired) return null;

    this._isChecking = true;
    this._lastCheckTime = now;
    try {
      const url = `api/inbox.php?action=check&accountId=${encodeURIComponent(auth.accountId)}&secret=${encodeURIComponent(auth.secret)}`;
      const res = await fetch(url);
      if (res.status === 401) {
        await handleUnauthorizedIdentity();
        return null;
      }
      if (!res.ok) return null;

      const data = await res.json().catch(() => null);
      if (data?.ok && data.status) {
        this.unreadCount = Number(data.status.unreadCount) || 0;
        this.unclaimedGifts = Number(data.status.unclaimedGifts) || 0;
        this.hasHighPriority = !!data.status.hasHighPriority;
        this._lastCheckTime = Date.now();
        this._notify();
        return data.status;
      }
    } catch {
      // Network offline / silent fallback
    } finally {
      this._isChecking = false;
    }
    return null;
  }

  async fetchMessages() {
    const auth = getAuth();
    if (!auth?.accountId || !auth?.secret || auth.isExpired) return [];

    try {
      const url = `api/inbox.php?action=list&accountId=${encodeURIComponent(auth.accountId)}&secret=${encodeURIComponent(auth.secret)}`;
      const res = await fetch(url);
      if (res.status === 401) {
        await handleUnauthorizedIdentity();
        return [];
      }
      if (!res.ok) return [];

      const data = await res.json().catch(() => null);
      if (data?.ok && Array.isArray(data.messages)) {
        this.messages = data.messages;
        this._recomputeCounts();
        this._notify();
        return this.messages;
      }
    } catch (err) {
      console.warn('[InboxManager] fetchMessages failed:', err);
    }
    return [];
  }

  _recomputeCounts() {
    let unread = 0;
    let unclaimed = 0;
    let highPri = false;

    for (const m of this.messages) {
      if (!m.isRead) unread++;
      if (m.rewards && !m.isClaimed) unclaimed++;
      if (m.priority === 'popup' && (!m.isRead || (m.rewards && !m.isClaimed))) {
        highPri = true;
      }
    }

    this.unreadCount = unread;
    this.unclaimedGifts = unclaimed;
    this.hasHighPriority = highPri;
  }

  async markAsRead(messageId) {
    const auth = getAuth();
    if (!auth?.accountId || !auth?.secret) return false;

    // Optimistically mark locally
    const m = this.messages.find(msg => msg.id === messageId);
    if (m && !m.isRead) {
      m.isRead = true;
      this._recomputeCounts();
      this._notify();
    }

    try {
      await fetch('api/inbox.php?action=read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accountId: auth.accountId,
          secret: auth.secret,
          messageId
        })
      });
      return true;
    } catch {
      return false;
    }
  }

  async claimReward(messageId) {
    const auth = getAuth();
    if (!auth?.accountId || !auth?.secret) {
      return { ok: false, error: 'unauthorized', message: 'Operative authentication required.' };
    }

    try {
      const res = await fetch('api/inbox.php?action=claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accountId: auth.accountId,
          secret: auth.secret,
          messageId
        })
      });

      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) {
        return {
          ok: false,
          error: data?.error || 'claim_failed',
          message: data?.message || 'Failed to claim allocation.'
        };
      }

      // Apply rewards to wallet and inventory
      const rewards = data.rewards || {};
      if (rewards.fugazi && Number(rewards.fugazi) > 0) {
        wallet.addFugazi(Number(rewards.fugazi), 'transmission_drop');
      }
      if (rewards.tickets && Number(rewards.tickets) > 0) {
        wallet.addTickets(Number(rewards.tickets), 'transmission_drop');
      }
      if (rewards.packs && Number(rewards.packs) > 0) {
        inventory.addBoosterPacks(Number(rewards.packs));
      }

      // Trigger OCC cloud save
      schedulePush(1500);

      // Update local message record
      const msg = this.messages.find(m => m.id === messageId);
      if (msg) {
        msg.isClaimed = true;
        msg.isRead = true;
        this._recomputeCounts();
        this._notify();
      } else {
        await this.checkInbox({ force: true });
      }

      return {
        ok: true,
        title: data.title || 'Supply Drop',
        rewards
      };
    } catch (err) {
      return {
        ok: false,
        error: 'network_error',
        message: 'Network error while claiming supply drop.'
      };
    }
  }

  async redeemVoucher(code) {
    const cleanCode = (code || '').trim();
    if (!cleanCode) {
      return { ok: false, error: 'missing_code', message: 'Clearance code cannot be empty.' };
    }

    const auth = getAuth();
    if (!auth?.accountId || !auth?.secret) {
      return { ok: false, error: 'unauthorized', message: 'Operative authentication required.' };
    }

    try {
      const res = await fetch('api/inbox.php?action=redeem-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accountId: auth.accountId,
          secret: auth.secret,
          code: cleanCode
        })
      });

      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) {
        return {
          ok: false,
          error: data?.error || 'redeem_failed',
          message: data?.message || 'Clearance code is invalid or already redeemed.'
        };
      }

      // Apply rewards
      const rewards = data.rewards || {};
      if (rewards.fugazi && Number(rewards.fugazi) > 0) {
        wallet.addFugazi(Number(rewards.fugazi), 'voucher_drop');
      }
      if (rewards.tickets && Number(rewards.tickets) > 0) {
        wallet.addTickets(Number(rewards.tickets), 'voucher_drop');
      }
      if (rewards.packs && Number(rewards.packs) > 0) {
        inventory.addBoosterPacks(Number(rewards.packs));
      }

      schedulePush(1500);
      this.checkInbox({ force: true });

      return {
        ok: true,
        code: data.code,
        title: data.title || 'Clearance Voucher',
        rewards
      };
    } catch (err) {
      return {
        ok: false,
        error: 'network_error',
        message: 'Network error while redeeming voucher code.'
      };
    }
  }
}

export const inboxManager = new InboxManager();
